import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http-service.service';
import { ApiResponse } from '../common/api-response.types';
import { createReservationIdempotencyKey } from '../utils/reservation-idempotency-key.util';

export interface CreateReservationDto {
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  amount_rate: number;
  client_id?: number;
  branch_id?: number;
  currency_from_id?: number;
  currency_to_id?: number;
  amount_reserved?: number;
  status?: string;
  address_id?: number;
  admin_user_id?: number;
  quotation_id?: number;
  reservation_code?: string;
  promotion_id?: number;
  promotional_foreign_amount?: number;
  promotional_rate?: number;
  split_group_id?: string;
  normal_quotation_id?: number;
  promo_amount_reserved?: number;
  normal_amount_reserved?: number;
  /** Client-generated idempotency key (or use Idempotency-Key header). */
  request_id?: string;
}

export interface ActiveReservationNotice {
  id: number;
  reservationCode: string | null;
  foreignAmount: number;
  currencyCode: string;
  exchangeRate: number;
  branchName: string;
  expiryDate: string | null;
  collapsed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReservationsService {
  private readonly reservationsEndpoint = '/reservations';
  private readonly activeNoticeCollapsedSubject = new BehaviorSubject(false);

  /** True while the user has an active reservation whose notice is minimized. */
  public readonly activeNoticeCollapsed$ =
    this.activeNoticeCollapsedSubject.asObservable();

  constructor(private readonly httpService: HttpService) {}

  public setActiveNoticeCollapsed(collapsed: boolean): void {
    this.activeNoticeCollapsedSubject.next(collapsed);
  }

  public create(body: CreateReservationDto): Observable<ApiResponse<any>> {
    const requestId =
      body.request_id?.trim() || createReservationIdempotencyKey();
    const payload: CreateReservationDto = {
      ...body,
      request_id: requestId,
    };

    return this.httpService.post<ApiResponse<any>>(
      this.reservationsEndpoint,
      payload,
      { 'Idempotency-Key': requestId },
    );
  }

  public getById(id: number): Observable<ApiResponse<any>> {
    return this.httpService.get<ApiResponse<any>>(
      `${this.reservationsEndpoint}/${id}`,
    );
  }

  /** Listado operativo (admin/staff: todas; usuario: solo las suyas). */
  public getAll(): Observable<ApiResponse<any>> {
    return this.httpService.get<ApiResponse<any>>(this.reservationsEndpoint);
  }

  /** Mis reservaciones: siempre filtradas por el cliente = usuario autenticado. */
  public getMine(): Observable<ApiResponse<any>> {
    return this.httpService.get<ApiResponse<any>>(
      `${this.reservationsEndpoint}/me`,
    );
  }

  public getActiveNotice(): Observable<
    ApiResponse<ActiveReservationNotice | null>
  > {
    return this.httpService.get<ApiResponse<ActiveReservationNotice | null>>(
      `${this.reservationsEndpoint}/me/active-notice`,
    );
  }

  public dismissActiveNotice(
    id: number,
  ): Observable<ApiResponse<{ id: number }>> {
    return this.httpService.put<ApiResponse<{ id: number }>>(
      `${this.reservationsEndpoint}/${id}/dismiss-active-notice`,
      {},
    );
  }

  public restoreActiveNotice(
    id: number,
  ): Observable<ApiResponse<{ id: number }>> {
    return this.httpService.put<ApiResponse<{ id: number }>>(
      `${this.reservationsEndpoint}/${id}/restore-active-notice`,
      {},
    );
  }

  public cancel(id: number): Observable<ApiResponse<any>> {
    return this.httpService.put<ApiResponse<any>>(
      `${this.reservationsEndpoint}/${id}`,
      { status: 'REFUSED_BY_USER' },
    );
  }

  public estimatePoints(body: {
    currencyFromCode: string;
    currencyToCode: string;
    amountReserved: number;
    amountRate?: number;
    toAmount?: number;
    fromBuyRate?: number;
    toSellRate?: number;
  }): Observable<ApiResponse<{ estimatedPoints: number }>> {
    return this.httpService.post<ApiResponse<{ estimatedPoints: number }>>(
      `${this.reservationsEndpoint}/estimate-points`,
      body,
    );
  }
}
